import React from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import FoldableSection from '~/components/Common/Blocks/FoldableSection';

import { ENABLE_CLUSTERING_EDITING } from '../constants';
import { LereTabComponentProps } from '../types';

import ClusterMapCanvas from './ClusterMapCanvas';
import ClusteringParamsPanel from './ClusteringParamsPanel';
import ManualClustersPanel from './ManualClustersPanel';
import { onNumberChange } from './clusterMapUtils';
import { useClusterMap } from './useClusterMap';

export default function ClusterMap({ onLereError }: LereTabComponentProps) {
  const { state, derived, ui, actions } = useClusterMap({ onLereError });
  const {
    clusterings,
    selectedRun,
    runParamsDisplay,
    activeClustersCount,
    hasActiveClusterData,
    visibleClusters,
    noiseSummary,
    mapCenter,
    mapZoom,
    clusterOverlays,
    selectedCluster,
    selectedClusterColor,
    manualPickPolyline,
    manualPickPolygon,
    fillOpacity,
    manualClusterValidation,
    manualClusterIds,
    canAddManualCluster,
    errorMessage,
  } = derived;
  const { isClusteringsLoading, isLoading, isFetching, isPreviewPending } = ui;
  const {
    setState,
    setManualPickPoints,
    updateNewManualCluster,
    runChange,
    enterEdit,
    preview,
    exitEdit,
    selectCluster,
    goToManualCluster,
    manualPickComplete,
    togglePickingBounds,
    clearPickPoints,
    toggleManualClusterEnabled,
    toggleManualClusterProtected,
    addManualCluster,
  } = actions;

  const {
    selectedClusterId,
    selectedRunId,
    showRunParameters,
    previewData,
    isEditing,
    cutKm,
    minClusterSize,
    minSamples,
    maxAssignDistKm,
    noisePolicy,
    opacity,
    maxClusters,
    minClusterOffers,
    showBorderBreakingClusters,
    manualClustersDraft,
    isPickingManualBounds,
    manualPickPoints,
    newManualCluster,
  } = state;

  return (
    <Stack spacing={4}>
      {errorMessage && !ENABLE_CLUSTERING_EDITING && (
        <Alert severity="info">Clustering controls are coming soon.</Alert>
      )}

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
        {ENABLE_CLUSTERING_EDITING ? (
          <FormControl size="small" sx={{ minWidth: 300 }}>
            <InputLabel>Clustering</InputLabel>
            <Select
              value={selectedRunId}
              label="Clustering"
              onChange={(e) => runChange(e.target.value)}
              disabled={isClusteringsLoading}
            >
              {clusterings.map((run, index) => (
                <MenuItem key={run.run_id} value={run.run_id}>
                  {isEditing ? 'Custom' : index === 0 ? 'Latest (live)' : new Date(run.created_at).toLocaleString()}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <>
            <Typography variant="subtitle1">Latest clustering</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedRun ? new Date(selectedRun.created_at).toLocaleString() : 'No clustering runs available yet'}
            </Typography>
          </>
        )}

        {ENABLE_CLUSTERING_EDITING && (
          <Stack direction="row" spacing={1} alignItems="center">
            {!isEditing ? (
              <>
                <Button variant="contained" color="primary" onClick={enterEdit} disabled={isPreviewPending}>
                  Edit
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Won't impact production map
                </Typography>
              </>
            ) : (
              <>
                <Button variant="contained" color="primary" onClick={preview} disabled={isPreviewPending}>
                  Apply
                </Button>
                <Button variant="text" color="primary" onClick={exitEdit} disabled={isPreviewPending}>
                  Exit edit mode
                </Button>
                {previewData && (
                  <Typography variant="caption" color="text.secondary">
                    Preview applied (not saved)
                  </Typography>
                )}
              </>
            )}
          </Stack>
        )}

        {(isLoading || isFetching) && <CircularProgress size={24} />}
      </Stack>

      {ENABLE_CLUSTERING_EDITING && isEditing && (
        <ClusteringParamsPanel
          cutKm={cutKm}
          minClusterSize={minClusterSize}
          minSamples={minSamples}
          noisePolicy={noisePolicy}
          maxAssignDistKm={maxAssignDistKm}
          onCutKmChange={(value) => setState({ cutKm: value })}
          onMinClusterSizeChange={(value) => setState({ minClusterSize: value })}
          onMinSamplesChange={(value) => setState({ minSamples: value })}
          onNoisePolicyChange={(value) => setState({ noisePolicy: value })}
          onMaxAssignDistKmChange={(value) => setState({ maxAssignDistKm: value })}
        >
          <ManualClustersPanel
            clusters={manualClustersDraft}
            newCluster={newManualCluster}
            isPickingBounds={isPickingManualBounds}
            manualPickPoints={manualPickPoints}
            manualClusterValidation={manualClusterValidation}
            manualClusterIds={manualClusterIds}
            canAddManualCluster={canAddManualCluster}
            onToggleClusterEnabled={toggleManualClusterEnabled}
            onToggleCluster={toggleManualClusterProtected}
            onGoToCluster={goToManualCluster}
            onTogglePickingBounds={togglePickingBounds}
            onClearPickPoints={clearPickPoints}
            onNewClusterChange={updateNewManualCluster}
            onAddCluster={addManualCluster}
          />
        </ClusteringParamsPanel>
      )}

      <Stack spacing={2}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          🗺️ Map Display
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            label="opacity"
            type="number"
            size="small"
            value={opacity}
            onChange={onNumberChange((value) => setState({ opacity: value }))}
            slotProps={{ htmlInput: { step: 0.05, min: 0, max: 1 } }}
          />
          <TextField
            label="max_clusters"
            type="number"
            size="small"
            value={maxClusters}
            onChange={onNumberChange((value) => setState({ maxClusters: value }))}
            slotProps={{ htmlInput: { min: 0 } }}
            helperText="Max clusters to render (0 = all)"
          />
          <TextField
            label="min_cluster_offers"
            type="number"
            size="small"
            value={minClusterOffers}
            onChange={onNumberChange((value) => setState({ minClusterOffers: value }))}
            slotProps={{ htmlInput: { min: 0 } }}
            helperText="Hide clusters with fewer than X offers"
          />
        </Stack>
        <FormControlLabel
          control={
            <Checkbox
              checked={showBorderBreakingClusters}
              onChange={(e) => setState({ showBorderBreakingClusters: e.target.checked })}
              size="small"
            />
          }
          label="Show border-spanning clusters"
        />
      </Stack>

      <FoldableSection
        title="Run Parameters"
        titleComponent="h6"
        manualState={showRunParameters}
        onChange={(open) => setState({ showRunParameters: open })}
      >
        {selectedRun ? (
          <>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
              <Typography variant="body2">Algo: {selectedRun.algo_version || '—'}</Typography>
              <Typography variant="body2">Config: {selectedRun.clustering_config_id}</Typography>
              <Typography variant="body2">Run ID: {selectedRun.run_id}</Typography>
            </Stack>
            {runParamsDisplay ? (
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'divider',
                  overflowX: 'auto',
                  fontSize: 12,
                  fontFamily: 'monospace',
                }}
              >
                {JSON.stringify(runParamsDisplay, null, 2)}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                No run parameters available.
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No clustering run available.
          </Typography>
        )}
      </FoldableSection>

      {hasActiveClusterData && (
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
          <Typography variant="body2" color="text.secondary">
            {visibleClusters.length} shown / {activeClustersCount} total clusters
          </Typography>
          {ENABLE_CLUSTERING_EDITING && noiseSummary && (
            <Typography variant="body2" color="text.secondary">
              Noise points: {noiseSummary.noiseCount} ({noiseSummary.noisePct.toFixed(1)}% of {noiseSummary.totalPoints}
              )
            </Typography>
          )}
        </Stack>
      )}

      <ClusterMapCanvas
        center={mapCenter}
        zoom={mapZoom}
        overlays={clusterOverlays}
        fillOpacity={fillOpacity}
        selectedClusterId={selectedClusterId}
        selectedCluster={selectedCluster}
        selectedClusterColor={selectedClusterColor}
        onSelectCluster={selectCluster}
        isPickingManualBounds={ENABLE_CLUSTERING_EDITING ? isPickingManualBounds : false}
        manualPickPoints={ENABLE_CLUSTERING_EDITING ? manualPickPoints : []}
        manualPickPolyline={ENABLE_CLUSTERING_EDITING ? manualPickPolyline : null}
        manualPickPolygon={ENABLE_CLUSTERING_EDITING ? manualPickPolygon : null}
        onManualPickPointsChange={ENABLE_CLUSTERING_EDITING ? setManualPickPoints : () => undefined}
        onManualPickComplete={ENABLE_CLUSTERING_EDITING ? manualPickComplete : () => undefined}
      />
    </Stack>
  );
}
